// src/components/communication/ChatInputBar.js
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Send,
  Paperclip,
  Image as ImageIcon,
  X,
  AlertCircle,
} from "lucide-react";
import MentionAutocomplete from "./MentionAutocomplete";

function ChatInputBar({
  activeChannel,
  usersList = [],
  operatorsList = [],
  teamsList = [],
  onSendMessage,
  initialText = "",
  onClearInitialText,
}) {
  const [inputText, setInputText] = useState(initialText || "");
  const [attachments, setAttachments] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Sincroniza se vier texto inicial (ex: clicou em uma sugestão do assistente IA)
  useEffect(() => {
    if (initialText) {
      setInputText(initialText);
      inputRef.current?.focus();
      if (onClearInitialText) onClearInitialText();
    }
  }, [initialText, onClearInitialText]);

  // Cola imagens/arquivos da área de transferência
  const handlePaste = useCallback((e) => {
    if (e.clipboardData && e.clipboardData.files && e.clipboardData.files.length > 0) {
      const pastedFiles = Array.from(e.clipboardData.files);
      setAttachments((prev) => [...prev, ...pastedFiles]);
    }
  }, []);

  // Mudança de texto leve com detecção de @mention
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputText(value);
    if (sendError) setSendError(null);

    const cursor = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursor);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex !== -1 && !/\s/.test(textBeforeCursor.slice(lastAtIndex + 1))) {
      const query = textBeforeCursor.slice(lastAtIndex + 1);
      setMentionQuery(query);
      setSelectedMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  // Seleção de Menção
  const handleSelectMention = (item) => {
    if (!inputRef.current) return;
    const cursor = inputRef.current.selectionStart;
    const textBeforeCursor = inputText.slice(0, cursor);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");
    const textAfterCursor = inputText.slice(cursor);

    const newText =
      inputText.slice(0, lastAtIndex) +
      `@${item.tag} ` +
      textAfterCursor;

    setInputText(newText);
    setMentionQuery(null);
    inputRef.current.focus();
  };

  // Envio de mensagem
  const handleSend = async (e) => {
    e?.preventDefault();
    setSendError(null);

    if (!activeChannel) {
      setSendError("Nenhum canal selecionado. Selecione um canal antes de enviar.");
      return;
    }

    if (!inputText.trim() && attachments.length === 0) return;
    if (sending) return;

    setSending(true);
    try {
      // Extrai menções de usuários e operadores do texto
      const mentionsUsers = [];
      const mentionsOperators = [];

      usersList.forEach((u) => {
        const tag = u.username || u.name?.replace(/\s+/g, "_").toLowerCase();
        if (inputText.includes(`@${tag}`) || (u.username && inputText.includes(`@${u.username}`))) {
          mentionsUsers.push(u.id);
        }
      });

      operatorsList.forEach((op) => {
        const tag = op.nome?.replace(/\s+/g, "_").toLowerCase();
        if (inputText.includes(`@${tag}`)) {
          mentionsOperators.push(op.id);
        }
      });

      // Extrai menções de equipes
      const mentionsTeams = [];
      const normalizeTag = (str) =>
        (str || "")
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "");

      teamsList.forEach((team) => {
        const norm = normalizeTag(team.nome);
        if (
          inputText.includes(`@equipe_${norm}`) ||
          inputText.includes(`@time_${norm}`) ||
          (team.slug && inputText.includes(`@${team.slug}`))
        ) {
          mentionsTeams.push(team);
        }
      });

      await onSendMessage({
        content: inputText,
        files: attachments,
        mentionsUsers,
        mentionsOperators,
        mentionsTeams,
      });

      setInputText("");
      setAttachments([]);
      setMentionQuery(null);
      setSendError(null);
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
      const msg =
        err?.data?.message ||
        err?.message ||
        "Não foi possível salvar a mensagem. Verifique a permissão do banco.";
      setSendError(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border-t border-slate-200 bg-white">
      {/* ALERTA DE ERRO NO ENVIO */}
      {sendError && (
        <div className="px-4 py-2 bg-rose-50 border-b border-rose-200 text-rose-700 text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle size={14} className="shrink-0" />
            <span>{sendError}</span>
          </div>
          <button
            type="button"
            onClick={() => setSendError(null)}
            className="text-rose-400 hover:text-rose-700 cursor-pointer"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* PREVIEW DE ANEXOS SELECIONADOS */}
      {attachments.length > 0 && (
        <div className="px-4 py-2 bg-slate-100 border-b border-slate-200 flex items-center gap-2 overflow-x-auto">
          <span className="text-[11px] font-bold text-slate-600 shrink-0">Anexos:</span>
          {attachments.map((file, i) => (
            <div
              key={i}
              className="flex items-center gap-1 bg-white border border-slate-200 px-2 py-1 rounded-lg text-xs text-slate-700 shrink-0"
            >
              <ImageIcon size={12} className="text-indigo-600" />
              <span className="truncate max-w-[120px]">{file.name}</span>
              <button
                type="button"
                onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                className="text-slate-400 hover:text-rose-500 cursor-pointer"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* CAIXA DE ENTRADA DO CHAT */}
      <div className="p-3 relative">
        {/* AUTOCOMPLETE DE MENÇÕES @ */}
        {mentionQuery !== null && (
          <MentionAutocomplete
            searchQuery={mentionQuery}
            usersList={usersList}
            operatorsList={operatorsList}
            teamsList={teamsList}
            onSelect={handleSelectMention}
            onClose={() => setMentionQuery(null)}
            selectedIndex={selectedMentionIndex}
          />
        )}

        <form onSubmit={handleSend} className="flex items-end gap-2">
          <input
            type="file"
            multiple
            ref={fileInputRef}
            onChange={(e) => {
              if (e.target.files?.length) {
                setAttachments((prev) => [...prev, ...Array.from(e.target.files)]);
              }
            }}
            className="hidden"
            accept="image/*,.pdf,.xlsx,.csv,.txt"
          />

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-indigo-600 transition shrink-0 cursor-pointer"
            title="Anexar Fotos ou Arquivos (ou use Ctrl+V)"
          >
            <Paperclip size={16} />
          </button>

          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              rows={2}
              placeholder={
                activeChannel
                  ? `Mensagem em #${activeChannel.display_name || activeChannel.name}... Ex: #71740, [71740], sku.DIR-TRI, @equipe ou @nome`
                  : "Selecione um canal acima para digitar..."
              }
              value={inputText}
              onChange={handleInputChange}
              onPaste={handlePaste}
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && mentionQuery === null) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              className="w-full px-3.5 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={sending || (!inputText.trim() && attachments.length === 0)}
            className="p-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm shadow-indigo-600/30 transition disabled:opacity-50 shrink-0 cursor-pointer"
          >
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}

export default React.memo(ChatInputBar);
